import { getEntityById, getLabelFromId } from '@core/utils/collection';
import { idToForm, optionalNumber, toFormValue } from '@core/utils/form';
import type { CreateStoleItemData, StoleItem } from '../../../../api/order';
import { VestmentCategory } from '../../../enum/vestmentCategory';
import { SIZE_FIELDS_BY_VESTMENT_CATEGORY } from '../../size';
import { getStoleFeatures } from '../../stole';
import {
  EMPTY_LINE_ITEM_BASE,
  baseFieldsFromApi,
  baseLineItemPayload,
  fabricSegment,
  formatOrderItemSizeParts,
  materialSegment,
  segment,
  segments,
} from '../helpers';
import type { OrderItemCategoryConfig, StoleItemFormValues } from '../types';

export const stoleConfig: OrderItemCategoryConfig = {
  label: 'Stole',
  emptyFormValues: (): StoleItemFormValues => ({
    ...EMPTY_LINE_ITEM_BASE,
    length: '',
    stoleCut: '',
    panelFabric: '',
    panelColor: '',
    withCord: false,
    cord: '',
    withTassel: false,
    tassel: '',
    withPatching: false,
    patchingFabric: '',
    patchingColor: '',
  }),
  apiToFormValues: (item): StoleItemFormValues => {
    const stole = item as StoleItem;
    return {
      ...baseFieldsFromApi(stole),
      length: toFormValue(stole.length),
      stoleCut: idToForm(stole.stoleCut),
      panelFabric: idToForm(stole.panelFabric),
      panelColor: idToForm(stole.panelColor),
      withCord: Boolean(stole.cord),
      cord: idToForm(stole.cord),
      withTassel: Boolean(stole.tassel),
      tassel: idToForm(stole.tassel),
      withPatching: Boolean(stole.patchingFabric || stole.patchingColor),
      patchingFabric: idToForm(stole.patchingFabric),
      patchingColor: idToForm(stole.patchingColor),
    };
  },
  formToPayload: (item, vestmentType): CreateStoleItemData => {
    const stole = item as StoleItemFormValues;
    const features = getStoleFeatures(vestmentType);
    const withPatching = features?.patching !== false && stole.withPatching;
    return {
      ...baseLineItemPayload(stole),
      length: optionalNumber(stole.length),
      stoleCut: features?.hasCuts ? optionalNumber(stole.stoleCut) : null,
      panelFabric: optionalNumber(stole.panelFabric),
      panelColor: optionalNumber(stole.panelColor),
      cord: stole.withCord ? optionalNumber(stole.cord) : null,
      tassel: stole.withTassel ? optionalNumber(stole.tassel) : null,
      patchingFabric: withPatching
        ? optionalNumber(stole.patchingFabric)
        : null,
      patchingColor: withPatching ? optionalNumber(stole.patchingColor) : null,
    };
  },
  formatSummarySegments: (item, ctx) => {
    const stole = item as StoleItemFormValues;
    const vestment = getEntityById(ctx.vestments, stole.vestment);
    const features = getStoleFeatures(vestment?.type);

    return segments(
      segment(
        'Size',
        ...formatOrderItemSizeParts(
          SIZE_FIELDS_BY_VESTMENT_CATEGORY[VestmentCategory.Stole],
          {
            sizeName: getLabelFromId(ctx.sizes, stole.size),
            size: getEntityById(ctx.sizes, stole.size),
            customer: ctx.customer,
            vestmentType: vestment?.type,
            length: stole.length,
          }
        )
      ),
      features?.hasCuts
        ? segment(
            'Type',
            getLabelFromId(vestment?.stoleCuts ?? [], stole.stoleCut)
          )
        : null,
      fabricSegment(
        'Panel',
        ctx.fabrics,
        ctx.colors,
        stole.panelFabric,
        stole.panelColor
      ),
      stole.withCord
        ? materialSegment('Cord', ctx.cords, stole.cord)
        : segment('Cord', 'Without'),
      stole.withTassel
        ? materialSegment('Tassel', ctx.tassels, stole.tassel)
        : segment('Tassel', 'Without'),
      features?.patching === false
        ? null
        : stole.withPatching
          ? fabricSegment(
              'Patching',
              ctx.fabrics,
              ctx.colors,
              stole.patchingFabric,
              stole.patchingColor
            )
          : segment('Patching', 'Without')
    );
  },
};
